import { TemplatePortal } from '@angular/cdk/portal';
import {
  afterNextRender,
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  resource,
  signal,
  TemplateRef,
  viewChild,
  ViewContainerRef
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslatePipe } from '@cmi/addin-core';
import { LoadingSpinner, Notification } from '@cmi/ui';
import { saveDocument } from '../../../lib/word-document';
import { App } from '../../app';
import {
  deleteCustomXmlPart,
  getDefaultFormValues,
  jumpToSection,
  loadFormValuesFromDocument,
  saveCustomXmlPart,
  serializeToXml
} from './copy-contents.lib';
import { FormValues } from './copy-contents.models';

@Component({
  selector: 'cmi-copy-contents',
  imports: [LoadingSpinner, Notification, ReactiveFormsModule, TranslatePipe],
  templateUrl: './copy-contents.html'
})
export class CopyContents implements OnDestroy, OnInit {
  private readonly _viewContainerRef = inject(ViewContainerRef);
  private readonly _appComponent = inject(App);

  private readonly _footerPortal = viewChild.required<TemplateRef<unknown>>('footerPortal');
  private readonly _notificationPortal = viewChild<TemplateRef<unknown>>('notificationPortal');
  private _portal: TemplatePortal<unknown> | null = null;
  private _notificationPortalInstance: TemplatePortal<unknown> | null = null;
  private _notificationTimeoutId: ReturnType<typeof setTimeout> | null = null;
  protected readonly form = new FormGroup({
    sources: new FormControl('', {
      nonNullable: true,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      validators: [Validators.required]
    }),
    areas: new FormControl('', {
      nonNullable: true,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      validators: [Validators.required]
    }),
    targets: new FormControl(1, {
      nonNullable: true,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      validators: [Validators.required, Validators.min(0)]
    }),
    pageBreak: new FormControl(false, { nonNullable: true }),
    copyContentsExecute: new FormControl(false, { nonNullable: true }),
    manualPageNumber: new FormControl(false, { nonNullable: true })
  });

  private _initialFormValues: FormValues | null = null;
  protected readonly hasChanges = signal(false);
  protected readonly isLoading = signal(true);
  protected readonly isDeleting = signal(false);

  private readonly _saveSuccess = signal<boolean>(false);
  private readonly _saveFormValues = signal<FormValues | null>(null);
  private _wasLoading = false;

  private readonly _deleteSuccess = signal<boolean>(false);
  protected readonly deleteError = signal<Error | string | null>(null);

  protected readonly notificationMode = signal<'info' | 'error' | null>(null);
  protected readonly notificationMessageKey = signal<string | null>(null);
  protected readonly notificationDescriptionKey = signal<string | null>(null);
  protected readonly notificationErrorMessage = signal<string | null>(null);

  protected readonly saveResource = resource<void, { xmlString: string }>({
    params: () => {
      const formValues = this._saveFormValues();
      if (formValues) {
        return {
          xmlString: serializeToXml(formValues)
        };
      }

      return {
        xmlString: ''
      };
    },
    loader: async ({ params }) => {
      if (params.xmlString) {
        await Word.run(async (context) => {
          await saveCustomXmlPart(context, params.xmlString);
        });
      }
    }
  });

  constructor() {
    this.form.valueChanges.subscribe(() => {
      this._checkForChanges();
    });

    effect(() => {
      const isLoading = this.saveResource.isLoading();
      const error = this.saveResource.error();

      if (this._wasLoading && !isLoading && this._saveFormValues() !== null) {
        if (error) {
          this._saveSuccess.set(false);
          this._showNotification(
            'error',
            'copyContents.saveError',
            'copyContents.saveErrorDescription'
          );
        } else {
          const formValues = this.form.getRawValue();
          this._initialFormValues = { ...formValues };
          this._checkForChanges();
          this._saveSuccess.set(true);
          this._showNotification('info', 'copyContents.saveSuccess');

          this._saveFormValues.set(null);

          void saveDocument();
        }
      }

      this._wasLoading = isLoading;
    });

    effect(() => {
      const loading = this.isLoading();
      if (!loading && this._portal) {
        this._appComponent.footerPortal.set(this._portal);
      } else {
        this._appComponent.footerPortal.set(null);
      }
    });

    afterNextRender(() => {
      this._portal = new TemplatePortal(this._footerPortal(), this._viewContainerRef);
      if (!this.isLoading() && this._portal) {
        this._appComponent.footerPortal.set(this._portal);
      }

      if (this._notificationPortal()) {
        this._notificationPortalInstance = new TemplatePortal(
          this._notificationPortal()!,
          this._viewContainerRef
        );
      }
    });
  }

  public ngOnDestroy(): void {
    if (this._notificationTimeoutId !== null) {
      clearTimeout(this._notificationTimeoutId);
      this._notificationTimeoutId = null;
    }

    this._appComponent.footerPortal.set(null);
    this._appComponent.notificationPortal.set(null);
  }

  public ngOnInit(): void {
    void this._setDefaultValues();
  }

  public onJumpToSection(): void {
    const targetValue = this.form.controls.targets.value;
    if (targetValue > 0) {
      void jumpToSection(targetValue).catch((error) => {
        console.error('Error jumping to section:', error);
        this._showNotification(
          'error',
          'copyContents.jumpError',
          'copyContents.jumpErrorDescription'
        );
      });
    }
  }

  private async _setDefaultValues(): Promise<void> {
    this.isLoading.set(true);
    this._appComponent.footerPortal.set(null);

    try {
      const loadedValues = await Word.run(async (context) => {
        return await loadFormValuesFromDocument(context);
      });

      if (loadedValues) {
        this._initialFormValues = { ...loadedValues };
        this.form.patchValue(loadedValues, { emitEvent: false });
      } else {
        const defaultValues = getDefaultFormValues();
        this._initialFormValues = { ...defaultValues };
        this.form.patchValue(defaultValues, { emitEvent: false });
      }
    } catch (error: unknown) {
      console.error(
        'Error loading form values from document:',
        error instanceof Error ? error.message : String(error)
      );

      const defaultValues = getDefaultFormValues();
      this._initialFormValues = { ...defaultValues };
      this.form.patchValue(defaultValues, { emitEvent: false });
    } finally {
      this.isLoading.set(false);
      this._checkForChanges();

      if (this._portal) {
        this._appComponent.footerPortal.set(this._portal);
      }
    }
  }

  private _checkForChanges(): void {
    if (!this._initialFormValues) {
      const currentValues = this.form.getRawValue();
      const defaultValues = getDefaultFormValues();
      this.hasChanges.set(!this._areValuesEqual(currentValues, defaultValues));
      return;
    }

    const currentValues = this.form.getRawValue();
    this.hasChanges.set(!this._areValuesEqual(currentValues, this._initialFormValues));
  }

  private _areValuesEqual(a: FormValues, b: FormValues): boolean {
    return (
      a.sources === b.sources &&
      a.areas === b.areas &&
      a.targets === b.targets &&
      a.pageBreak === b.pageBreak &&
      a.copyContentsExecute === b.copyContentsExecute &&
      a.manualPageNumber === b.manualPageNumber
    );
  }

  protected async onDelete(): Promise<void> {
    this.isDeleting.set(true);
    this._deleteSuccess.set(false);
    this.deleteError.set(null);

    try {
      await Word.run(async (context) => await deleteCustomXmlPart(context));
      const defaultValues = getDefaultFormValues();
      this.form.reset(defaultValues);
      this._initialFormValues = { ...defaultValues };
      this._checkForChanges();
      this._deleteSuccess.set(true);
      this._showNotification('info', 'copyContents.deleteSuccess');

      void saveDocument();
    } catch (error: unknown) {
      console.error(
        'Error deleting CustomXmlPart:',
        error instanceof Error ? error.message : String(error)
      );
      this.deleteError.set(error instanceof Error ? error : String(error));
      this._showNotification(
        'error',
        'copyContents.deleteError',
        'copyContents.deleteErrorDescription'
      );
    } finally {
      this.isDeleting.set(false);
    }
  }

  protected onSubmit(): void {
    if (this.form.invalid) {
      return;
    }

    this._saveSuccess.set(false);
    this._saveFormValues.set({ ...this.form.getRawValue() });
    this.saveResource.reload();
  }

  private _extractErrorMessage(): string | undefined {
    const error = this.saveResource.error() || this.deleteError();
    if (!error) {
      return undefined;
    }
    return error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : String(error);
  }

  private _clearNotification(): void {
    this._notificationTimeoutId = null;
    this._appComponent.notificationPortal.set(null);
    this.notificationMode.set(null);
    this.notificationMessageKey.set(null);
    this.notificationDescriptionKey.set(null);
    this.notificationErrorMessage.set(null);
  }

  private _showNotification(
    mode: 'info' | 'error',
    messageKey: string,
    descriptionKey?: string
  ): void {
    if (!this._notificationPortalInstance) {
      return;
    }

    const errorMessage = mode === 'error' ? this._extractErrorMessage() : undefined;

    this.notificationMode.set(mode);
    this.notificationMessageKey.set(messageKey);
    this.notificationDescriptionKey.set(descriptionKey || null);
    this.notificationErrorMessage.set(errorMessage || null);

    this._appComponent.notificationPortal.set(this._notificationPortalInstance);
    if (this._notificationTimeoutId !== null) {
      clearTimeout(this._notificationTimeoutId);
    }

    this._notificationTimeoutId = setTimeout(() => this._clearNotification(), 3000);
  }
}
